/**
 * SOAP Note Thunks
 * Async actions for fetching, saving, and managing SOAP notes
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as soapNoteApi from '../api/soapNoteApi';
import {
  ensureSoapOrderCacheSync,
  getSoapOrderCacheEpoch,
  getSoapPaidInitialEpoch,
  loadSoapOrder,
} from '../soapOrderCache';

/**
 * Fetch a SOAP note by its _id and store it under the given tabKey.
 * The linked order and its paid-initial check come in the same answer and go to the
 * SOAP order cache (soapOrderCacheSlice), so the note view doesn't ask for them after
 * it renders. A backend that doesn't send the order yet: the order is asked for here,
 * as soon as the note names it.
 */
export const fetchSoapNote = createAsyncThunk(
  'emr/fetchSoapNote',
  async ({ tabKey, soapNoteId }, { dispatch, getState, rejectWithValue }) => {
    try {
      ensureSoapOrderCacheSync(dispatch);
      const orderEpoch = getSoapOrderCacheEpoch(getState);
      const paidEpoch = getSoapPaidInitialEpoch(getState);
      const data = await soapNoteApi.fetchSoapNote(soapNoteId, { includeOrder: true });
      const treatmentId = data?.soapNote?.treatmentId;
      if (treatmentId && !('order' in data)) {
        loadSoapOrder(dispatch, getState, treatmentId).catch(() => {});
      }
      return {
        tabKey,
        data,
        receivedAt: Date.now(),
        // Not cached if anything was dropped while the note was on its way
        orderCacheable: getSoapOrderCacheEpoch(getState) === orderEpoch,
        paidCacheable: getSoapPaidInitialEpoch(getState) === paidEpoch,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Save (create or update) a SOAP note.
 * payload should include: patientId, date, tabKey, and any section content.
 * If _id is in payload, it's an update.
 *
 * Safety net: if the caller omits _id but Redux already has one for this
 * tabKey (from a prior save that completed), inject it here. This prevents
 * duplicate-creation race conditions where two concurrent saveField calls
 * both fire without an _id before the first one's response arrives.
 *
 * _ifUnchanged: send the note's updatedAt as this tab has it as expectedUpdatedAt, so
 * the server refuses the save if the note changed since. A refused save rejects with
 * { conflict: true, soapNote } (the stored note). The tab passes the updatedAt it last
 * had from the server (_expectedUpdatedAt); without it, the one in Redux is sent.
 */
export const saveSoapNote = createAsyncThunk(
  'emr/saveSoapNote',
  async ({ tabKey, _savedField, _ifUnchanged, _expectedUpdatedAt, ...payload }, { getState, rejectWithValue }) => {
    try {
      const existingNote = getState().emr?.soapNotesByKey?.[tabKey];
      // Inject existing noteId if the caller didn't provide one
      if (!payload.noteId) {
        if (existingNote?._id) {
          payload = { ...payload, noteId: existingNote._id };
        }
      }
      if (_ifUnchanged) {
        const expectedUpdatedAt = _expectedUpdatedAt !== undefined ? _expectedUpdatedAt : existingNote?.updatedAt;
        payload = { ...payload, expectedUpdatedAt: expectedUpdatedAt ?? null };
      }
      const isCreation = !payload.noteId;
      const data = await soapNoteApi.saveSoapNote(payload);
      return { tabKey, data, _savedField, isCreation };
    } catch (error) {
      if (error.response?.status === 409) {
        return rejectWithValue({ ...error.response.data, conflict: true });
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch custom shortcut templates (for the single-dot menu)
 */
export const fetchShortcuts = createAsyncThunk(
  'emr/fetchShortcuts',
  async (_, { rejectWithValue }) => {
    try {
      const data = await soapNoteApi.fetchShortcuts();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch pre-built section templates by type (for the double-dot menu)
 */
export const fetchSoapTemplates = createAsyncThunk(
  'emr/fetchSoapTemplates',
  async (type, { rejectWithValue }) => {
    try {
      const data = await soapNoteApi.fetchSoapTemplates(type);
      return { type, data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
